import type { ApolloError } from "@apollo/client";
import { nanoid } from "nanoid";
import { type FC, useCallback, useEffect, useState } from "react";
import { CardSeparator, LoadingSpinner, VStack } from "widgets";
import { type GetArrayElementType, sortAlpha } from "widgets/utils";

import { graphql } from "../api/client.ts";
import { PubSubSharedActions } from "../messaging/pubsub-actions.ts";
import { ProjectsSelect } from "./ProjectsSelect.tsx";
import { type ItemTagUpdateData, TagListItem } from "./TagListItem.tsx";

type Tag = GetArrayElementType<Awaited<ReturnType<typeof graphql.getTags>>["itemTags"]>;

export interface TagManagementProps {
  /**
   * Optional. If not specified, the management form will show a project select
   */
  projectId?: number;
}

export const TagManagement: FC<TagManagementProps> = ({ projectId }) => {
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(projectId ?? null);
  const [tags, setTags] = useState<Tag[] | null>();

  const handleTagUpdate = useCallback(() => {
    if (selectedProjectId) {
      graphql.getTags({ projectId: selectedProjectId }).then(({ itemTags }) => {
        setTags(itemTags ? itemTags.sort((a, b) => sortAlpha(a.name, b.name)) : []);
      });
    }
  }, [selectedProjectId]);

  const handleCreateTag = useCallback(
    async (tagData: ItemTagUpdateData) => {
      if (!selectedProjectId) {
        return;
      }

      try {
        await graphql.addTag({
          itemTagInputData: {
            colour: tagData.colour,
            name: tagData.name,
            projectId: selectedProjectId,
          },
        });

        handleTagUpdate();
        PubSub.publish(PubSubSharedActions.UPDATE_TAGS);
      } catch (error) {
        // throw the error so that the submitter (TagListItem) can catch and show it
        throw new Error((error as ApolloError)?.message);
      }
    },
    [handleTagUpdate, selectedProjectId],
  );

  const handleUpdateTag = useCallback(
    async (tagId: number, tagData: ItemTagUpdateData) => {
      if (!selectedProjectId) {
        return;
      }

      try {
        await graphql.addTag({
          itemTagInputData: {
            id: tagId,
            colour: tagData.colour,
            name: tagData.name,
            projectId: selectedProjectId,
          },
        });
      } catch (error) {
        // throw the error so that the submitter (TagListItem) can catch and show it
        throw new Error((error as ApolloError)?.message);
      }

      handleTagUpdate();
      PubSub.publish(PubSubSharedActions.UPDATE_TAGS);
    },
    [handleTagUpdate, selectedProjectId],
  );

  const handleDeleteTag = useCallback(
    async (tagId: number) => {
      try {
        await graphql.deleteTag({ itemTagId: tagId });
        handleTagUpdate();
        PubSub.publish(PubSubSharedActions.UPDATE_TAGS);
      } catch (error) {
        // throw the error so that the submitter (TagListItem) can catch and show it
        throw new Error((error as ApolloError)?.message);
      }
    },
    [handleTagUpdate],
  );

  useEffect(() => {
    handleTagUpdate();
  }, [handleTagUpdate]);

  return (
    <>
      {!projectId && (
        <>
          <ProjectsSelect
            label={"Select a project"}
            isRequired
            initialSelectedProjectId={projectId}
            onSelectionChange={(project) => {
              if (project) {
                setSelectedProjectId(project.id);
              }
            }}
          />
          <CardSeparator />
        </>
      )}
      <VStack>
        {!tags ? (
          <LoadingSpinner />
        ) : tags?.length > 0 ? (
          tags.map((tag) => (
            <TagListItem
              tag={tag}
              key={`tag-item-${tag.id}`}
              onUpdate={(tagData) => handleUpdateTag(tag.id, tagData)}
              onDelete={() => handleDeleteTag(tag.id)}
            />
          ))
        ) : (
          <>No tags found.</>
        )}
        {/* Use additional margin on separator because we do not want a rowGap of 15 between the tags as we have in normal cards */}
        {selectedProjectId && (
          <>
            <CardSeparator style={{ margin: "10px 0" }} />
            <TagListItem onCreate={handleCreateTag} key={`create-tag-input-${nanoid()}`} />
          </>
        )}
      </VStack>
    </>
  );
};
